import { Component, OnDestroy, OnInit } from '@angular/core';
import { Role } from '../shared/models/role';
import { Operation } from '../shared/models/operation';
import { RoleOperationService } from '../services/role-operation.service';
import { ToastService } from '../shared/toast/toast.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { UserRoleService } from '../services/user-role.service';
import { OperationAccessSettingActionComponent } from './operation-access-setting-action.component';
import { MenuItem } from 'primeng/api';
import { SessionService } from '../services/session.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { SubSink } from 'subsink';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'pps-role-operation-management',
  templateUrl: './role-operation-management.component.html',
  styleUrls: ['./role-operation-management.component.css']
})
export class RoleOperationManagementComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  spinnerName: string = "spinner";
  spinnerText: string = "Loading data ...";
 
  smallScreen: boolean = false;

  items: MenuItem[] = [
    {label: "Admin", icon: "pi-fw fas fa-user-lock"},
    {label: "Role Permissions", icon: "pi-fw fas fa-user-clock"}
  ];

  loading: boolean = false;

  roles: Role[] = [];
  roleGridHeaders: any = [];

  roleOperations: Operation[] = [];
  roleOperationGridHeaders: any = [];

  operations: Operation[] = [];

  context:any = {};
  selectedRole: Role = null;

  private roleGridApi: any;
  roleOperationGridApi: any;

  constructor(private _userRoleService: UserRoleService,
              private _roleOperationService: RoleOperationService,
              private _sessionService: SessionService,
              private _toastService: ToastService,
              private _util: PpsUtilityService,
              private _spinner: NgxSpinnerService,
              private breakpointObserver: BreakpointObserver) {
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });    
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.context = { componentParent: this };
    this.roleGridHeaders = this.getRoleGridHeader();
    this.loadRoles();

    this.roleOperationGridHeaders = this.getRoleOperationGridHeader();
  }

  onRoleGridReady(params) {
    this.roleGridApi = params.api;
  }

  onRoleOperationGridReady(params) {
    this.roleOperationGridApi = params.api;
  }

  onRoleSelectionChanged(params){
    var selectedRows = this.roleGridApi.getSelectedRows();
    this.selectedRole = selectedRows.length === 1 ? selectedRows[0] : null;
    this.loadRoleOperations(this.selectedRole.id);
  }

  loadRoles(){
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._userRoleService.getRoles()
      .subscribe(res => {
        if (res.succeed){
          this.roles = res.result;
        }else{
          this._toastService.failed("Load roles failed: " + res.errors.join(";"));
        }
      })
      .add(()=>this._spinner.hide(this.spinnerName));
  }

  loadRoleOperations(roleId: number){
    this.loading = true;
    this.roleOperations = [];
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._roleOperationService.getOperationsByRole(roleId)
      .subscribe(
        res => {
          if (res.succeed){
            this.roleOperations = res.result;
          }else{
            this._toastService.failed("Load role operations failed: " + res.errors.join(";"));
          }
        }, _ => { }, () => this.loading = false)
        .add(()=>this._spinner.hide(this.spinnerName));
  }

  updateRoleOperationSetting(roleOperation: Operation){
    if (roleOperation.hasAccess){
      this.subs.sink = this._roleOperationService.addOperationToRole(this.selectedRole.id, roleOperation.id)
        .subscribe(res=>{
          if (res.succeed){
            this._sessionService.refreshUserContext();
            this._toastService.succeeded(`Permission [${roleOperation.operationName}] granted to role [${this.selectedRole.roleName}]`);
          }else{
            this._toastService.succeeded(`Grant permission [${roleOperation.operationName}] to role [${this.selectedRole.roleName}] failed: ` + res.errors.join(";"));
          }
        });
    }else{
      this.subs.sink = this._roleOperationService.deleteOperationFromRole(this.selectedRole.id, roleOperation.id)
        .subscribe(res=>{
          if (res.succeed){
            this._sessionService.refreshUserContext();
            this._toastService.succeeded(`Permission [${roleOperation.operationName}] revoked from role [${this.selectedRole.roleName}]`);
          }else{
            this._toastService.succeeded(`Revoke permission [${roleOperation.operationName}] from role [${this.selectedRole.roleName}] failed: ` + res.errors.join(";"));
          }
        });
    }
  }

  private getRoleGridHeader(){
    var cols = [
      {
        headerName: 'Role',
        field:'roleName',
        filter: true,
        sortable: true,
        width: 180
      },
      {
        headerName: 'Total Users',
        field:'userCount',
        filter: false,
        sortable: true,
        width: 100,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => this._util.formatNumber(params.data[params.colDef.field], 0)
      },
      {
        headerName: 'Role Description',
        field:'roleDescription',
        filter: false,
        sortable: false,
        flex: 1
      }
    ];

    if (this.smallScreen){
      cols[cols.length-1].flex = 0;
      cols[cols.length-1].width = 500;
    }
    
    return cols;
  }

  private getRoleOperationGridHeader(){
    return [
      {
        headerName: 'Allow Access',
        filter: false,
        sortable: false,
        width: 100,
        cellRenderer: OperationAccessSettingActionComponent,
        cellStyle: this.getCellStyleWithCenterAlignment
      },
      {
        headerName: 'Allow/Deny',
        width: 100,
        cellRenderer: params => params.data.hasAccess ? "Allow" : "Deny",
        cellStyle: this.getCellStyleWithCenterAlignment
      },
      {
        headerName: 'Permissions',
        field: 'operationName',
        filter: true,
        floatingFilter: true,
        sortable: true,
        width: 200,
        resizable: true,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Description',
        field: 'description',
        filter: false,
        sortable: false,
        flex: 1,
        cellStyle: this.getCellStyle
      }
    ]
  }

  private getCellStyleWithCenterAlignment(params){
    var style: any = {};
    if(params.data.hasAccess){
      style = {textAlign:'center', backgroundColor: '#44aa44', color: '#ffffff'};
    }else{
      style = {textAlign:'center'};
    }

    if (!params.data.businessReady){
      style.fontWeight = 'bold';
    }    

    return style;
  }

  private getCellStyle(params){
    var style: any = {};
    if (!params.data.businessReady){
      style = {fontWeight: 'bold'};
    }
    if(params.data.hasAccess){
      style.backgroundColor = "#44aa44";
      style.color = "#ffffff";
    }

    return style;
  }
}
